import {apiDelete, apiGet, apiPost, apiPut} from './client'
import {placePath} from './tree'
import type {TreeIndex} from './types'

/* Место в Gramps — узел ИЕРАРХИИ: Страна → Город → Улица. Связь задаётся
 * через placeref_list (ссылка на родителя), у объекта своё короткое имя
 * и тип.
 *
 * Импорт GEDCOM иерархию теряет: тег PLAC это одна строка через запятую,
 * поэтому после импорта все места были плоскими, с полным адресом в title
 * и без родителей. Разобрано миграцией 05.08.2026.
 *
 * Плоский title больше не заполняем: полное название собирается по цепочке
 * родителей (placePath), а вторая копия только разъезжалась бы с ней. */

export interface PlaceRaw {
  handle: string
  gramps_id?: string
  title?: string
  name?: {value?: string}
  place_type?: string | {string?: string; value?: number}
  placeref_list?: {ref?: string}[]
  [k: string]: unknown
}

/** Тип места: у пользовательских (value 0) смысл несёт строка.
 *  «Кладбища» в наборе Gramps нет, а для учёта захоронений он нужен. */
export interface PlaceTypeSel {
  value: number
  string: string
}

export const PLACE_TYPES: PlaceTypeSel[] = [
  {value: 1, string: ''},
  {value: 2, string: ''},
  {value: 4, string: ''},
  {value: 16, string: ''},
  {value: 7, string: ''},
  {value: 19, string: ''},
  {value: 0, string: 'Кладбище'},
  {value: -1, string: ''},
]

export const typeLabel = (t: PlaceTypeSel): string =>
  t.value === 0 ? t.string : typeRu(TYPE_BY_VALUE[t.value] || '') || 'Не указан'

const TYPE_BY_VALUE: Record<number, string> = {
  1: 'Country', 2: 'State', 4: 'City', 16: 'Village', 7: 'Street', 19: 'Building', [-1]: 'Unknown',
}

/** Координаты Gramps хранит строкой и понимает «55.7558», «N55.7558»,
 *  «55°45'21"N». А вот «55,7558» через запятую НЕ понимает — молча теряет.
 *  По-русски набирают именно так, поэтому запятую меняем на точку сами. */
export const normCoord = (s: string): string => s.trim().replace(',', '.')

export const badCoord = (s: string): boolean => {
  const v = normCoord(s)
  if (!v) return false
  const decimal = /^[NSEW]?-?\d+(\.\d+)?$/i.test(v)
  const dms = /^\d+°/.test(v)
  return !decimal && !dms
}

/** API отдаёт тип английской строкой — переводим для показа. */
const TYPE_RU: Record<string, string> = {
  Country: 'Страна',
  State: 'Регион / область',
  Province: 'Регион / область',
  Region: 'Регион / область',
  City: 'Город',
  Town: 'Город',
  Village: 'Село',
  Hamlet: 'Село',
  Street: 'Улица / адрес',
  Building: 'Здание',
  Number: 'Дом',
  Locality: 'Местность',
  District: 'Район',
  Municipality: 'Муниципалитет',
  County: 'Округ',
  Parish: 'Приход',
  Farm: 'Хутор',
  Unknown: '',
}
export const typeRu = (t: string): string => TYPE_RU[t] ?? t

/** Числовое значение типа по английскому имени — для подстановки в форму. */
const TYPE_VALUE: Record<string, number> = {
  Country: 1, State: 2, Province: 8, Region: 9, City: 4, Town: 15,
  Village: 16, Hamlet: 17, Street: 7, Building: 19, Number: 20,
  Locality: 6, District: 12, Municipality: 14, County: 3, Parish: 5, Farm: 18,
}
export const typeValue = (t: string): number => TYPE_VALUE[t] ?? -1

/** Создаёт место и возвращает handle. Номер назначает сервер. */
export async function createPlace(
  name: string,
  type: PlaceTypeSel = {value: -1, string: ''},
  parent: string | null = null,
  coords: {lat?: string; long?: string} = {},
): Promise<string> {
  const res = await apiPost<{handle?: string; _class?: string}[]>('/places/', {
    _class: 'Place',
    title: '',
    name: {_class: 'PlaceName', value: name.trim(), lang: ''},
    place_type: {_class: 'PlaceType', value: type.value, string: type.string},
    placeref_list: parent ? [{_class: 'PlaceRef', ref: parent}] : [],
    lat: normCoord(coords.lat || ''),
    long: normCoord(coords.long || ''),
  })
  const h = (res || []).find(i => i._class === 'Place')?.handle || res?.[0]?.handle
  if (!h) throw new Error('Сервер не вернул идентификатор созданного места')
  return h
}

/** Правка места: имя, тип, родитель. Любое поле можно не менять. */
export async function updatePlace(
  handle: string,
  patch: {name?: string; type?: PlaceTypeSel; parent?: string | null; lat?: string; long?: string},
): Promise<void> {
  const full = await apiGet<PlaceRaw>(`/places/${handle}`)
  const next: Record<string, unknown> = {...full, title: ''}
  if (patch.name !== undefined) {
    next.name = {...(full.name || {}), _class: 'PlaceName', value: patch.name.trim()}
  }
  if (patch.lat !== undefined) next.lat = normCoord(patch.lat)
  if (patch.long !== undefined) next.long = normCoord(patch.long)
  if (patch.type !== undefined) {
    next.place_type = {_class: 'PlaceType', value: patch.type.value, string: patch.type.string}
  }
  if (patch.parent !== undefined) {
    next.placeref_list = patch.parent ? [{_class: 'PlaceRef', ref: patch.parent}] : []
  }
  await apiPut(`/places/${handle}`, next)
}

/** Кто ссылается на место. Пустой список — место можно удалять. */
export async function placeUsers(handle: string): Promise<string[]> {
  const p = await apiGet<PlaceRaw>(`/places/${handle}`, {backlinks: 1})
  const bl = (p.backlinks || {}) as Record<string, string[]>
  return Object.values(bl).flat()
}

export async function deletePlace(handle: string): Promise<void> {
  await apiDelete(`/places/${handle}`)
}

/**
 * Сколько людей связано с каждым местом — по событиям из индекса, чтобы
 * не дёргать backlinks для каждого места отдельно.
 *
 * Считаем С УЧЁТОМ вложенных: человек с «ул. Красных Партизан» попадает
 * и в Мерефу, и в Украину. Иначе у города стоял бы ноль при полудюжине
 * жителей на его улицах.
 *
 * Копим множества, а не числа: один человек может быть привязан и к улице,
 * и к городу отдельными событиями — считать его дважды нельзя.
 */
export function placePeople(ix: TreeIndex): Map<string, Set<string>> {
  const byPlace = new Map<string, Set<string>>()

  const addUp = (place: string, person: string) => {
    let h: string | null = place
    // ограничение глубины — защита от кольца во вложенности
    for (let i = 0; h && i < 10; i++) {
      let s = byPlace.get(h)
      if (!s) byPlace.set(h, (s = new Set()))
      s.add(person)
      h = ix.places.get(h)?.parent ?? null
    }
  }

  for (const p of ix.people.values()) {
    for (const r of p.event_ref_list || []) {
      const e = ix.events.get(r.ref)
      if (e?.place) addUp(e.place, p.handle)
    }
  }
  return byPlace
}

export const placeUsage = (ix: TreeIndex): Map<string, number> =>
  new Map([...placePeople(ix)].map(([h, s]) => [h, s.size]))

/** Плоский список для выпадающих: [handle, «Мерефа, Украина»]. */
export const placeOptions = (ix: TreeIndex): [string, string][] =>
  [...ix.places.keys()]
    .map(h => [h, placePath(ix, h)] as [string, string])
    .filter(([, t]) => t)
    .sort((a, b) => a[1].localeCompare(b[1], 'ru'))
